import { createTheme } from '@mui/system';
import type { ThemeOptions } from '@mui/material/styles';

export const MBL_SIZE = "(max-width: 599px)";
export const TBL_SIZE = "(min-width: 600px) and (max-width: 1279px)";
export const DSK_SIZE = "(min-width: 1280px)";

export const MEDIAQUERY_MOBILE = `@media ${MBL_SIZE} `;
export const MEDIAQUERY_TABLET = `@media ${TBL_SIZE}`;
export const MEDIAQUERY_DESKTOP = `@media ${DSK_SIZE}`;

export const MEDIAQUERY_ORIENTATION_PORTRAIT = "and (orientation: portrait) ";
export const MEDIAQUERY_ORIENTATION_LANDSCAPE = "and (orientation: landscape) ";

export const HIGH_PIXEL_RATIO = "(-webkit-min-device-pixel-ratio: 2)";
export const LOW_PIXEL_RATIO = "(-webkit-max-device-pixel-ratio: 1.99)";

export const HIGH_RESOLUTION = "(min-resolution: 192dpi)";
export const LOW_RESOLUTION = "(max-resolution: 191dpi)";

export const DESKTOP_WIDTH = 1600;
export const TABLET_WIDTH = 1280;
export const MOBILE_WIDTH = 600;

// Create a theme instance.
// `as ThemeOptions` (not `as any`) is the narrowest cast that compiles: the object
// still carries (a) the MUI v4 `overrides:` key, which is absent from MUI v5's
// ThemeOptions type, and (b) custom palette keys (uiBlack, accentPolar, package1-4,
// containedButton, etc.) not declared in the PaletteOptions augmentation in types/mui.d.ts.
// A full cast removal is deferred to the MUI v7 flip — migrating `overrides` → `components`
// would re-activate currently-inert style overrides (a runtime behavior change), and
// augmenting the custom palette keys is a larger change, both out of scope here. Ref WAT-10876.
const dtvTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1280,
      lg: 1600,
      xl: 1920
    }
  },
  typography: {
    fontFamily: [
      // 'PFDinReg',
      // 'PFDinMed',
      "dtv-font",
      "sans-serif",
      "roboto"
    ].join(","),
    h1: {
      fontSize: "clamp(14px, 72*(100vw/1600), 72px)",
      fontWeight: 500,
      margin: "0em",
      lineHeight: "1.0em",
      letterSpacing: "-0.02em",
      [MEDIAQUERY_TABLET]: {
        fontSize: "clamp(14px, 55*(100vw/768), 60px)",
        lineHeight: "1.1em"
      },
      [MEDIAQUERY_MOBILE]: {
        fontSize: "clamp(14px, 35*(100vw/414), 38px)",
        lineHeight: "1.3em",
        letterSpacing: "normal"
      }
    },
    h2: {
      fontSize: "clamp(14px, 60*(100vw/1600), 60px)",
      fontWeight: 500,
      lineHeight: "1.1em",
      margin: "0em",
      letterSpacing: "-0.02em",
      [MEDIAQUERY_TABLET]: {
        fontSize: "clamp(14px, 45*(100vw/768), 49px)",
        lineHeight: "1.2em"
      },
      [MEDIAQUERY_MOBILE]: {
        fontSize: "clamp(14px, 29*(100vw/414), 31px)",
        lineHeight: "1.3em",
        letterSpacing: "normal"
      }
    },
    h3: {
      fontSize: "clamp(14px, 48*(100vw/1600), 48px)",
      fontWeight: 500,
      lineHeight: "1.2em",
      margin: "0em",
      letterSpacing: "-0.02em",
      [MEDIAQUERY_TABLET]: {
        fontSize: "clamp(14px, 35*(100vw/768), 38px)",
        lineHeight: "1.3em",
        letterSpacing: "normal"
      },
      [MEDIAQUERY_MOBILE]: {
        fontSize: "clamp(14px, 24*(100vw/414), 26px)",
        lineHeight: "1.3em",
        letterSpacing: "normal"
      }
    },
    h4: {
      fontSize: "clamp(14px, 36*(100vw/1600), 36px)",
      fontWeight: 500,
      lineHeight: "1.3em",
      margin: "0em",
      [MEDIAQUERY_TABLET]: {
        fontSize: "clamp(14px, 27*(100vw/768), 29px)"
      },
      [MEDIAQUERY_MOBILE]: {
        fontSize: "clamp(14px, 20*(100vw/414), 22px)",
        lineHeight: "1.5em"
      }
    },
    h5: {
      fontSize: "clamp(14px, 24*(100vw/1600), 24px)",
      fontWeight: 500,
      lineHeight: "1.3em",
      margin: "0em",
      [MEDIAQUERY_TABLET]: {
        fontSize: "clamp(14px, 20*(100vw/768), 22px)",
        lineHeight: "1.5em"
      },
      [MEDIAQUERY_MOBILE]: {
        fontSize: "clamp(14px, 16*(100vw/414), 17px)",
        lineHeight: "1.5em"
      }
    },
    h6: {
      lineHeight: "1.5em",
      fontSize: "14px",
      fontWeight: 500,
      margin: "0.25em 0em 0.75em"
    },
    subtitle1: {
      fontSize: "clamp(14px, 22*(100vw/1600), 22px)",
      fontWeight: 400,
      lineHeight: "1.5em",
      margin: "0.25em 0em 0.75em",
      [MEDIAQUERY_TABLET]: {
        fontSize: "clamp(14px, 18*(100vw/768), 19px)"
      },
      [MEDIAQUERY_MOBILE]: {
        fontSize: "clamp(14px, 16*(100vw/414), 17px)"
      }
    },
    subtitle2: {
      fontSize: "clamp(14px, 20*(100vw/1600), 20px)",
      fontWeight: 500,
      lineHeight: "1.5em",
      margin: "0.25em 0em 0.75em",
      [MEDIAQUERY_TABLET]: {
        fontSize: "clamp(14px, 16*(100vw/768), 17px)"
      },
      [MEDIAQUERY_MOBILE]: {
        fontSize: "clamp(14px, 14*(100vw/414), 15px)"
      }
    },
    body1: {
      fontSize: "clamp(14px, 18*(100vw/1600), 18px)",
      fontWeight: 400,
      lineHeight: "1.5em",
      margin: "0.25em 0em 0.75em",
      [MEDIAQUERY_TABLET]: {
        fontSize: "clamp(14px, 16*(100vw/768), 17px)"
      },
      [MEDIAQUERY_MOBILE]: {
        fontSize: "clamp(14px, 16*(100vw/414), 17px)"
      }
    },
    body2: {
      fontSize: "clamp(14px, 16*(100vw/1600), 16px)",
      fontWeight: 400,
      lineHeight: "1.5em",
      margin: "0.25em 0em 0.75em",
      [MEDIAQUERY_TABLET]: {
        fontSize: "clamp(14px, 14*(100vw/768), 15px)"
      },
      [MEDIAQUERY_MOBILE]: {
        fontSize: "clamp(14px, 14*(100vw/414), 15px)"
      }
    },
    button: {
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "1.5em",
      margin: "0em"
    },
    caption: {
      fontSize: "clamp(12px, 12*(100vw/1600),14px)",
      fontWeight: 300,
      lineHeight: "1.4em",
      margin: "0.25em 0em 0.5em",
      [MEDIAQUERY_TABLET]: {
        fontSize: "clamp(12px, 12*(100vw/768),14px)"
      },
      [MEDIAQUERY_MOBILE]: {
        fontSize: "clamp(12px, 12*(100vw/414),14px)"
      }
    },
    overline: {
      fontSize: "clamp(14px, 18*(100vw/1600), 18px)",
      fontWeight: 500,
      lineHeight: "1.5em",
      margin: "0em",
      [MEDIAQUERY_TABLET]: {
        fontSize: "clamp(14px, 16*(100vw/768), 17px)"
      },
      [MEDIAQUERY_MOBILE]: {
        fontSize: "clamp(14px, 14*(100vw/414), 15px)"
      }
    }
  },
  palette: {
    primary: {
      dark: "#102641",
      main: "#337ACE",
      light: "#76D4F8"
    },
    secondary: {
      dark: "#4B004E",
      main: "#78267B",
      light: "#B669AA"
    },
    uiBlack: {
      main: "#1D2329"
    },
    accentPolar: {
      main: "#76d4f8"
    },
    accentAmethyst: {
      main: "#78267B"
    },
    accentAmethystNew: {
      main: "#DC55ED"
    },
    accentSun: {
      main: "#E88534"
    },
    accentSolarFlare: {
      main: "#FDD746"
    },
    package1: {
      main: "#329EB4"
    },
    package2: {
      main: "#E88534"
    },
    package3: {
      main: "#7A267B"
    },
    package4: {
      main: "#21528C"
    },
    error: {
      main: "#C70032"
    },
    background: {
      dark: "#102641",
      default: "#fff"
    },
    surface: {
      main: "#FFFFFF"
    },
    onPrimary: {
      main: "#FFFFFF"
    },
    onSecondary: {
      main: "#FFFFFF"
    },
    onBackground: {
      main: "#FFFFFF"
    },
    onSurface: {
      main: "#000000"
    },
    onError: {
      main: "#FFFFFF"
    },
    textBlack: {
      main: "#000000"
    },
    textWhite: {
      main: "#FFFFFF"
    },
    textBlue: {
      main: "#337ACE"
    },
    black: {
      main: "#000000"
    },
    white: {
      main: "#FFFFFF"
    },
    red: {
      main: "#FF0000"
    },
    green: {
      main: "#00FF00"
    },
    blue: {
      main: "#0000FF"
    },
    yellow: {
      main: "#FFFF00"
    },
    magenta: {
      main: "#FF00FF"
    },
    aqua: {
      main: "#00FFFF"
    },
    functionalRed: {
      main: "#C70032"
    },
    functionalGreen: {
      main: "#2D7E24"
    },
    functionalOrange: {
      main: "#FF8500"
    },
    containedButton: {
      main: "#337ACE"
    },
    containedButtonLabel: {
      main: "#FFFFFF"
    },
    ozone: {
      main: "#337ACE",
      dark: "#2E70BD"
    },
    functionalOzone: {
      main: "#2E70BD"
    },
    lapis: {
      main: "#21528C"
    },
    turquoise: {
      main: "#1EBCF8"
    },
    polar: {
      main: "#76D4F8"
    },
    jet: {
      main: "#102641"
    }
  },
  overrides: {
    MuiCssBaseline: {
      "@global": {
        html: {
          scrollBehavior: "smooth"
        },
        "@font-face": [
          {
            fontFamily: "dtv-font",
            fontStyle: "normal",
            fontWeight: 400,
            fontDisplay: "swap",
            src: `url('/dtvassets/sales/uf/dtv-ui/fonts/PFDINTextPro-Regular.woff2') format('woff2')`
          },
          {
            fontFamily: "dtv-font",
            fontStyle: "normal",
            fontWeight: 300,
            fontDisplay: "swap",
            src: `url('/dtvassets/sales/uf/dtv-ui/fonts/PFDINTextPro-Light.woff2') format('woff2')`
          },
          {
            fontFamily: "dtv-font",
            fontStyle: "normal",
            fontWeight: 700,
            fontDisplay: "swap",
            src: `url('/dtvassets/sales/uf/dtv-ui/fonts/PFDINTextPro-Bold.woff2') format('woff2')`
          },
          {
            fontFamily: "dtv-font",
            fontStyle: "normal",
            fontWeight: 500,
            fontDisplay: "swap",
            src: `url('/dtvassets/sales/uf/dtv-ui/fonts/PFDINTextPro-Medium.woff2') format('woff2')`
          },
          // {
          //   fontFamily: "dtv-font-jubilat-italic",
          //   fontStyle: "italic",
          //   fontWeight: 700,
          //   fontDisplay: "swap",
          //   src: `url('/dtvassets/sales/uf/dtv-ui/fonts/Jubilat-BoldItalic.woff2') format('woff2')`,
          // },
          {
            fontFamily: "roboto-regular",
            fontStyle: "regular",
            fontWeight: 400,
            fontDisplay: "swap",
            src: `url('/dtvassets/global/fonts/Roboto-Regular.woff2') format('woff2')`
          },
          {
            fontFamily: "roboto-medium",
            fontStyle: "medium",
            fontWeight: 500,
            fontDisplay: "swap",
            src: `url('/dtvassets/global/fonts/Roboto-Medium.woff2') format('woff2')`
          },
          {
            fontFamily: "roboto-bold",
            fontStyle: "bold",
            fontWeight: 700,
            fontDisplay: "swap",
            src: `url('/dtvassets/global/fonts/Roboto-Bold.woff2') format('woff2')`
          }
        ]
      }
    },
    MuiPopover: {
      paper: {
        marginTop: "50px"
      }
    },
    MuiPaper: {
      elevation2: {
        boxShadow: null
      },
      elevation3: {
        boxShadow: null
      },
      elevation4: {
        boxShadow: null
      },
      elevation5: {
        boxShadow: null
      },
      elevation6: {
        boxShadow: null
      },
      elevation7: {
        boxShadow: null
      },
      elevation9: {
        boxShadow: null
      },
      elevation10: {
        boxShadow: null
      },
      elevation11: {
        boxShadow: null
      },
      elevation12: {
        boxShadow: null
      },
      elevation13: {
        boxShadow: null
      },
      elevation14: {
        boxShadow: null
      },
      elevation15: {
        boxShadow: null
      },
      elevation16: {
        boxShadow: null
      },
      elevation17: {
        boxShadow: null
      },
      elevation18: {
        boxShadow: null
      },
      elevation19: {
        boxShadow: null
      },
      elevation20: {
        boxShadow: null
      },
      elevation21: {
        boxShadow: null
      },
      elevation22: {
        boxShadow: null
      },
      elevation23: {
        boxShadow: null
      },
      elevation24: {
        boxShadow: null
      }
    },
    MuiTab: {
      root: {
        paddingTop: "0px",
        paddingBottom: "0px",
        textTransform: "uppercase",
        color: "#575757 !important",
        fontFamily: "roboto-medium", // "dtv-font",
        "&$selected": {
          color: "#21528C !important" // "#337ACE !important",
        },
        fontSize: "14px !important",
        fontWeight: "500",
        margin: "0px"
      }
    },
    MuiTabs: {
      indicator: {
        backgroundColor: "#337ACE",
        color: "#21528C" // "#337ACE",
      }
    },
    PrivateTabIndicator: {
      root: {
        height: "3px"
      }
    },
    MuiSelect: {
      icon: {
        color: "#000000"
      },
      outlined: {
        padding: "14.5px 32px 14.5px 14px !important"
      }
    },
    MuiOutlinedInput: {
      adornedStart: {
        paddingBottom: "10px",
        paddingTop: "8px"
      },
      input: {
        paddingLeft: "14px !important"
      },
      root: {
        "& $notchedOutline": {
          borderColor: "#E4E4E4"
        },
        "&$focused $notchedOutline": {
          border: "1px solid #337ACE !important",
          borderColor: "#337ACE"
        },
        "&$error $notchedOutline": {
          borderColor: "#c70032 !important"
        }
      },
      focused: {},
      notchedOutline: {}
    },
    MuiFormLabel: {
      root: {
        "&$focused": {
          color: "#337ACE"
        }
      },
      focused: {}
    },
    MuiAutocomplete: {
      // MultiSearch
      inputroot: {
        minHeight: "50px",
        paddingBottom: "0",
        paddingTop: "0"
      },
      listbox: {
        border: "1px solid #337ACE !important",
        borderRadius: "4px"
      },
      noOptions: {
        border: "1px solid #337ACE !important",
        borderRadius: "4px"
      }
    },
    MuiButtonBase: {
      root: {
        margin: "4px"
      }
    },
    MuiInputBase: {
      root: {
        color: "#666666",
        fontFamily: "dtv-font",
        fontSize: "16px",
        fontWeight: 400,
        position: "absolute",
        right: "4px"
      },
      input: {
        color: "#333333",
        padding: "6px !important"
      }
    },
    MuiChip: {
      root: {
        color: "#ffffff",
        backgroundColor: "#337ACE"
      },
      label: {
        fontFamily: "dtv-font",
        fontWeight: 500,
        fontSize: "14px"
      },
      deleteIcon: {
        color: "inherit"
      }
    },
    MuiIconButton: {
      root: {
        padding: "8px"
      }
    },
    MuiInputLabel: {
      outlined: {
        color: "#666666",
        fontFamily: "dtv-font",
        fontSize: "16px",
        fontWeight: 400,
        transform: "translate(14px, 17px) scale(1)",
        "&$shrink": {
          fontSize: "15px"
        }
      }
    },
    MuiSwitch: {
      switchBase: {
        margin: "0px"
      }
    },
    MuiTextField: {
      root: {
        '& input[type="text"]::-ms-clear': {
          display: "none"
        }
      }
    }
  }
} as ThemeOptions);
export default dtvTheme;
